import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { _get } from "../../../libraries/fetch";
import {
  ensureProductionApiToken,
  getRequestAuthToken
} from "../../../libraries/auth";
import {
  getMonthStatsURL,
  getTotalSalesDataURL,
  getNewMembersStatsURL,
  getDayPassStatsURL,
  getFrozenMemberStatsURL,
  getMonthWiseSalesChartDataURL,
  getTotalActiveMembersStatusURL
} from "../../../libraries/apiUrl";
import { fetchPosSaleOrders } from "./SalesOrders";
import { fetchDayPassSales } from "./DayPassSales";
import { fetchSubscriptionSales } from "./SubscriptionSales";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const MONTH_CHART_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - 5 + i);

const EMPTY_STATS = {
  totalSales: 0,
  totalPosSales: 0,
  totalDayPassSales: 0,
  newMembers: 0,
  totalActiveCustomers: 0,
  defaulters: 0,
  activeMembersThisMonth: 0,
  totalVisitors: 0,
  totalInquiry: 0,
  frozenMembers: 0,
  churnRate: 0,
  trainerSales: 0
};

const EMPTY_KPI = {
  current: null,
  previous: null,
  diff: 0,
  status: "NoDiff"
};

const KPI_KEYS = [
  "currentMonthSales",
  "previousMonthSales",
  "currentMonthNewMembers",
  "previousMonthNewMembers",
  "currentMonthVisitors",
  "previousMonthVisitors",
  "currentMonthFrozenMembers",
  "previousMonthFrozenMembers",
  "currentTotal",
  "previousTotal"
];

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[^0-9.-]+/g, "");
  const numericValue = Number(cleaned);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const looksLikeKpiPayload = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  KPI_KEYS.some((key) => Object.prototype.hasOwnProperty.call(value, key));

const unwrapPayload = (data) => {
  if (data === null || data === undefined) return null;
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return data;

  if (looksLikeKpiPayload(data)) return data;

  if (data.response !== undefined) {
    return unwrapPayload(data.response);
  }

  if (Array.isArray(data.data)) return data.data;
  if (data.data && typeof data.data === "object") {
    if (looksLikeKpiPayload(data.data)) return data.data;
    if (
      data.data.totalSales !== undefined ||
      data.data.activeCustomers !== undefined ||
      data.data._id
    ) {
      return data.data;
    }
  }

  if (Array.isArray(data.result)) return data.result;
  return data;
};

const isMonthStatsDoc = (doc) => {
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return false;
  if (doc.success === false || doc.status === false) return false;
  if (looksLikeKpiPayload(doc)) return false;
  return (
    doc.totalSales !== undefined ||
    doc.totalPosSales !== undefined ||
    doc.activeCustomers !== undefined ||
    doc.newMemberRegistered !== undefined ||
    doc.defaulters !== undefined ||
    doc.visitors !== undefined ||
    doc.enquiries !== undefined ||
    doc.branch !== undefined ||
    doc._id !== undefined ||
    (doc.month !== undefined && doc.year !== undefined)
  );
};

const getMonthStatsDoc = (data) => {
  const payload = unwrapPayload(data);
  if (!payload) return null;

  if (Array.isArray(payload)) {
    const doc = payload.find(isMonthStatsDoc) || payload[0] || null;
    return isMonthStatsDoc(doc) ? doc : null;
  }

  return isMonthStatsDoc(payload) ? payload : null;
};

const getPreviousMonth = (month, year) => {
  if (month <= 1) return { month: 12, year: year - 1 };
  return { month: month - 1, year };
};

const pickNumber = (...values) => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric !== null) return numeric;
  }
  return null;
};

const sumTrainerTotal = (trainerStats) => {
  if (!Array.isArray(trainerStats) || trainerStats.length === 0) return null;
  let total = 0;
  for (const trainer of trainerStats) {
    const pay = toNumber(trainer?.pay) ?? 0;
    const discount = toNumber(trainer?.discount) ?? 0;
    const tax = toNumber(trainer?.tax) ?? 0;
    total += pay + tax - discount;
  }
  return total;
};

const buildKpi = (currentRaw, previousRaw, { invert = false, isPercent = false } = {}) => {
  const current = toNumber(currentRaw);
  const previous = toNumber(previousRaw);

  if (current === null && previous === null) {
    return { ...EMPTY_KPI };
  }

  if (current === null) {
    return {
      current: null,
      previous,
      diff: previous ?? 0,
      status: "NoDiff"
    };
  }

  if (previous === null) {
    return {
      current,
      previous: null,
      diff: current,
      status: "NoDiff"
    };
  }

  const diff = current - previous;
  let status = "NoDiff";
  if (diff > 0) status = invert ? "Down" : "Up";
  if (diff < 0) status = invert ? "Up" : "Down";

  return {
    current,
    previous,
    diff: isPercent ? Number(Math.abs(diff).toFixed(2)) : Math.abs(diff),
    status
  };
};

const formatCount = (num) => {
  if (num === null || num === undefined) return "No Data";
  return Number(num).toLocaleString();
};

const formatCurrency = (num) => {
  if (num === null || num === undefined) return "No Data";
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const formatAmountInWords = (num) => {
  if (num === null || num === undefined) return "";
  const value = Number(num);
  if (!Number.isFinite(value)) return "";

  const sign = value < 0 ? "-" : "";
  let remaining = Math.round(Math.abs(value));
  const parts = [];

  const crore = Math.floor(remaining / 10_000_000);
  if (crore > 0) {
    parts.push(`${crore} ${crore === 1 ? "Crore" : "Crores"}`);
    remaining %= 10_000_000;
  }

  const lac = Math.floor(remaining / 100_000);
  if (lac > 0) {
    parts.push(`${lac} ${lac === 1 ? "Lac" : "Lacs"}`);
    remaining %= 100_000;
  }

  const thousand = Math.floor(remaining / 1_000);
  if (thousand > 0) {
    parts.push(`${thousand} Thousand`);
    remaining %= 1_000;
  }

  if (parts.length === 0) {
    return remaining > 0 ? `${sign}${remaining}` : "";
  }

  // Keep small leftover only when no larger units were used
  if (remaining > 0 && crore === 0 && lac === 0) {
    parts.push(String(remaining));
  }

  return `${sign}${parts.join(", ")}`;
};

const formatShortCurrency = (value) => {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  if (Math.abs(num) >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(Math.round(num));
};

const TrendIcon = ({ status, invertColors = false }) => {
  if (status === "Up") {
    const fill = invertColors ? "#e71831" : "#1ab394";
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="14" height="14" fill={fill} aria-hidden="true">
        <path d="M384 160c-17.7 0-32-14.3-32-32s14.3-32 32-32l160 0c17.7 0 32 14.3 32 32l0 160c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-82.7L342.6 374.6c-12.5 12.5-32.8 12.5-45.3 0L192 269.3 54.6 406.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l160-160c12.5-12.5 32.8-12.5 45.3 0L320 306.7 466.7 160 384 160z" />
      </svg>
    );
  }

  if (status === "Down") {
    const fill = invertColors ? "#1ab394" : "#e71831";
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="14" height="14" fill={fill} aria-hidden="true">
        <path d="M384 352c-17.7 0-32 14.3-32 32s14.3 32 32 32l160 0c17.7 0 32-14.3 32-32l0-160c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 82.7L342.6 137.4c-12.5-12.5-32.8-12.5-45.3 0L192 242.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0L320 205.3 466.7 352 384 352z" />
      </svg>
    );
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="14" height="14" fill="#f0ad4e" aria-hidden="true">
      <path d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z" />
    </svg>
  );
};

const metricFromMonthDoc = (doc, metricKey) => {
  if (!doc) return null;

  switch (metricKey) {
    case "sales":
      return pickNumber(doc.totalSales) ?? 0;
    case "posSales":
      return pickNumber(doc.totalPosSales) ?? 0;
    case "dayPassSales":
      return pickNumber(doc.totalDayPassSales, doc.visitorFee, doc.totalVisitorsSales) ?? 0;
    case "newMembers":
      return pickNumber(doc.newMemberRegistered) ?? 0;
    case "dayPass":
      return pickNumber(doc.visitors) ?? 0;
    case "frozen":
      return pickNumber(doc.totalFrozenMembers) ?? 0;
    case "activeMembers": {
      const active = pickNumber(doc.activeCustomers) ?? 0;
      const defaulters = pickNumber(doc.defaulters) ?? 0;
      return Math.max(active - defaulters, 0);
    }
    case "trainerSales":
      return sumTrainerTotal(doc.trainerStats) ?? 0;
    default:
      return 0;
  }
};

const buildYearMetricFromMonthStats = async (branchId, year, metricKey, upToMonth = 12) => {
  const responses = await Promise.all(
    Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      if (month > upToMonth) return Promise.resolve(null);
      return _get(getMonthStatsURL(branchId, month, year), { useCache: true });
    })
  );

  return responses.map((response, index) => {
    const month = index + 1;
    if (month > upToMonth) return null;
    return metricFromMonthDoc(getMonthStatsDoc(response), metricKey);
  });
};

const CHART_METRICS = {
  sales: {
    title: "Monthly Sales",
    seriesLabel: "Sales",
    chartType: "line",
    compareYears: true,
    currency: true,
    color: "#42A5F5",
    prevColor: "#9CCC65"
  },
  activeMembers: {
    title: "Total Active Members",
    seriesLabel: "Active Members",
    chartType: "bar",
    compareYears: false,
    currency: false,
    color: "#66BB6A",
    prevColor: "#A5D6A7"
  },
  newMembers: {
    title: "New Members Registered",
    seriesLabel: "New Members",
    chartType: "line",
    compareYears: true,
    currency: false,
    color: "#FFA726",
    prevColor: "#FFCC80"
  },
  dayPass: {
    title: "Day Pass / Visitors",
    seriesLabel: "Visitors",
    chartType: "line",
    compareYears: true,
    currency: false,
    color: "#26C6DA",
    prevColor: "#80DEEA"
  },
  frozen: {
    title: "Frozen Members",
    seriesLabel: "Frozen Members",
    chartType: "line",
    compareYears: true,
    currency: false,
    color: "#EF5350",
    prevColor: "#EF9A9A"
  },
  trainerSales: {
    title: "Trainer Sales",
    seriesLabel: "Trainer Sales",
    chartType: "line",
    compareYears: true,
    currency: true,
    color: "#AB47BC",
    prevColor: "#CE93D8"
  },
  posSales: {
    title: "POS Sales",
    seriesLabel: "POS Sales",
    chartType: "line",
    compareYears: true,
    currency: true,
    color: "#5C6BC0",
    prevColor: "#9FA8DA"
  },
  dayPassSales: {
    title: "Day Pass Sales",
    seriesLabel: "Day Pass Sales",
    chartType: "line",
    compareYears: true,
    currency: true,
    color: "#00897B",
    prevColor: "#80CBC4"
  }
};

const EMPTY_CHART_SERIES = {
  currentYear: CURRENT_YEAR,
  previousYear: CURRENT_YEAR - 1,
  currentYearData: Array(12).fill(null),
  previousYearData: Array(12).fill(null)
};

function KpiCard({
  title,
  currentLabel,
  previousLabel,
  diffLabel,
  status,
  invertColors = false,
  onClick,
  active = false,
  hint = ""
}) {
  const clickable = typeof onClick === "function";

  return (
    <div
      className={`admin-dashboard-kpi-item${clickable ? " is-clickable" : ""}${active ? " is-active" : ""}`}
      onClick={onClick}
      onKeyDown={clickable ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      } : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <h6>{title}</h6>
      <h1>{currentLabel}</h1>
      {hint ? <p className="admin-dashboard-kpi-hint">{hint}</p> : null}
      <div className="admin-dashboard-kpi-comp">
        <div className="admin-dashboard-kpi-change">
          <label className={`admin-dashboard-kpi-trend status-${status}`}>
            <TrendIcon status={status} invertColors={invertColors} />
            <span>{diffLabel}</span>
          </label>
          <p>Change</p>
        </div>
        <div className="admin-dashboard-kpi-prev">
          <label>{previousLabel}</label>
          <p>VS last Month</p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subValue = "" }) {
  return (
    <div className="admin-dashboard-stat-item">
      <span className="admin-dashboard-stat-label">{title}</span>
      <div className="admin-dashboard-stat-value-wrap">
        <span className="admin-dashboard-stat-value">{value}</span>
        {subValue ? <span className="admin-dashboard-stat-subvalue">{subValue}</span> : null}
      </div>
    </div>
  );
}

function Dashboard({ branchId, gymPackage = "" }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(false);
  const [hasStatsDoc, setHasStatsDoc] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [stats, setStats] = useState(EMPTY_STATS);
  const [kpis, setKpis] = useState({
    churn: { ...EMPTY_KPI },
    sales: { ...EMPTY_KPI },
    posSales: { ...EMPTY_KPI },
    dayPassSales: { ...EMPTY_KPI },
    activeMembers: { ...EMPTY_KPI },
    newMembers: { ...EMPTY_KPI },
    dayPass: { ...EMPTY_KPI },
    frozen: { ...EMPTY_KPI },
    trainerSales: { ...EMPTY_KPI }
  });
  const [activeChartKey, setActiveChartKey] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState("");
  const [chartSeries, setChartSeries] = useState(EMPTY_CHART_SERIES);

  const trainerSalesLabel = useMemo(() => {
    const pkg = String(gymPackage || "").toLowerCase().trim();
    if (pkg === "premium" || pkg === "enterprise") {
      return "Subscription Sales";
    }
    return "Trainer Sales";
  }, [gymPackage]);

  const fetchDashboardData = useCallback(async () => {
    const resolvedBranchId =
      typeof branchId === "object" && branchId !== null
        ? String(branchId._id || branchId.id || "").trim()
        : String(branchId || "").trim();

    if (!resolvedBranchId) {
      setFetchError("Waiting for branch...");
      return;
    }

    setLoading(true);
    setFetchError("");

    try {
      const token = await ensureProductionApiToken();
      const authToken = token || getRequestAuthToken();

      if (!authToken) {
        setFetchError("No API session token. Please log out and log in again, then reopen this branch.");
        setHasStatsDoc(false);
        setStats(EMPTY_STATS);
        return;
      }

      const prev = getPreviousMonth(selectedMonth, selectedYear);
      const monthStatsUrl = getMonthStatsURL(resolvedBranchId, selectedMonth, selectedYear);

      // Only call admin endpoints that exist on production.
      // churn-rate / total-pos-sales / active-members-status currently 404 on admin API.
      const [
        monthStatsData,
        prevMonthStatsData,
        salesData,
        newMembersData,
        dayPassData,
        frozenData,
        currentPosSales,
        previousPosSales,
        currentDayPassSales,
        previousDayPassSales,
        currentSubscriptionSales,
        previousSubscriptionSales
      ] = await Promise.all([
        _get(monthStatsUrl, { useCache: false }),
        _get(getMonthStatsURL(resolvedBranchId, prev.month, prev.year), { useCache: false }),
        _get(getTotalSalesDataURL(resolvedBranchId, selectedMonth, selectedYear), { useCache: false }),
        _get(getNewMembersStatsURL(resolvedBranchId, selectedMonth, selectedYear), { useCache: false }),
        _get(getDayPassStatsURL(resolvedBranchId, selectedMonth, selectedYear), { useCache: false }),
        _get(getFrozenMemberStatsURL(resolvedBranchId, selectedMonth, selectedYear), { useCache: false }),
        fetchPosSaleOrders(resolvedBranchId, selectedMonth, selectedYear),
        fetchPosSaleOrders(resolvedBranchId, prev.month, prev.year),
        fetchDayPassSales(resolvedBranchId, selectedMonth, selectedYear),
        fetchDayPassSales(resolvedBranchId, prev.month, prev.year),
        fetchSubscriptionSales(resolvedBranchId, selectedMonth, selectedYear),
        fetchSubscriptionSales(resolvedBranchId, prev.month, prev.year)
      ]);

      console.log("[Dashboard] month-stats URL:", monthStatsUrl);
      console.log("[Dashboard] month-stats response:", monthStatsData);
      console.log("[Dashboard] sales KPI response:", salesData);

      const monthDoc = getMonthStatsDoc(monthStatsData);
      const prevDoc = getMonthStatsDoc(prevMonthStatsData);
      setHasStatsDoc(Boolean(monthDoc));

      const anySuccess =
        monthStatsData !== null ||
        salesData !== null ||
        newMembersData !== null ||
        dayPassData !== null ||
        frozenData !== null ||
        !currentPosSales?.error ||
        !currentDayPassSales?.error ||
        !currentSubscriptionSales?.error;

      if (!anySuccess) {
        setFetchError("Dashboard APIs returned unauthorized/failed. Please log out and log in again.");
        setStats(EMPTY_STATS);
        setKpis({
          churn: { ...EMPTY_KPI },
          sales: { ...EMPTY_KPI },
          posSales: { ...EMPTY_KPI },
          dayPassSales: { ...EMPTY_KPI },
          activeMembers: { ...EMPTY_KPI },
          newMembers: { ...EMPTY_KPI },
          dayPass: { ...EMPTY_KPI },
          frozen: { ...EMPTY_KPI },
          trainerSales: { ...EMPTY_KPI }
        });
        return;
      }

      const salesPayload = unwrapPayload(salesData) || {};
      const newMembersPayload = unwrapPayload(newMembersData) || {};
      const dayPassPayload = unwrapPayload(dayPassData) || {};
      const frozenPayload = unwrapPayload(frozenData) || {};

      const currentSales = pickNumber(salesPayload.currentMonthSales, monthDoc?.totalSales);
      const previousSales = pickNumber(salesPayload.previousMonthSales, prevDoc?.totalSales);

      const currentPos = pickNumber(
        currentPosSales?.error ? null : currentPosSales?.total,
        monthDoc?.totalPosSales
      );
      const previousPos = pickNumber(
        previousPosSales?.error ? null : previousPosSales?.total,
        prevDoc?.totalPosSales
      );

      const currentDayPassSale = pickNumber(
        currentDayPassSales?.error ? null : currentDayPassSales?.total
      );
      const previousDayPassSale = pickNumber(
        previousDayPassSales?.error ? null : previousDayPassSales?.total
      );

      const currentNewMembers = pickNumber(
        newMembersPayload.currentMonthNewMembers,
        monthDoc?.newMemberRegistered
      );
      const previousNewMembers = pickNumber(
        newMembersPayload.previousMonthNewMembers,
        prevDoc?.newMemberRegistered
      );

      const currentVisitors = pickNumber(dayPassPayload.currentMonthVisitors, monthDoc?.visitors);
      const previousVisitors = pickNumber(dayPassPayload.previousMonthVisitors, prevDoc?.visitors);

      const currentFrozen = pickNumber(
        frozenPayload.currentMonthFrozenMembers,
        monthDoc?.totalFrozenMembers
      );
      const previousFrozen = pickNumber(
        frozenPayload.previousMonthFrozenMembers,
        prevDoc?.totalFrozenMembers
      );

      // Subscription / Trainer Sales = fee collection Summary "Total Sales"
      const currentTrainer = currentSubscriptionSales?.error
        ? null
        : pickNumber(currentSubscriptionSales?.total);
      const previousTrainer = previousSubscriptionSales?.error
        ? null
        : pickNumber(previousSubscriptionSales?.total);

      const totalActiveCustomers = pickNumber(monthDoc?.activeCustomers) ?? 0;
      const defaulters = pickNumber(monthDoc?.defaulters) ?? 0;
      const derivedActiveMembers = Math.max(totalActiveCustomers - defaulters, 0);
      const previousActiveCustomers = pickNumber(prevDoc?.activeCustomers) ?? 0;
      const previousDefaulters = pickNumber(prevDoc?.defaulters) ?? 0;
      const previousDerivedActive = prevDoc
        ? Math.max(previousActiveCustomers - previousDefaulters, 0)
        : null;

      const currentActive = monthDoc ? derivedActiveMembers : null;
      const previousActive = pickNumber(
        monthDoc?.prevMonthActiveMembers,
        previousDerivedActive
      );

      const currentChurn = pickNumber(monthDoc?.churnRate);
      const previousChurn = pickNumber(prevDoc?.churnRate);

      setStats({
        totalSales: currentSales ?? 0,
        totalPosSales: currentPos ?? 0,
        totalDayPassSales: currentDayPassSale ?? 0,
        newMembers: currentNewMembers ?? 0,
        totalActiveCustomers,
        defaulters,
        activeMembersThisMonth: currentActive ?? derivedActiveMembers,
        totalVisitors: currentVisitors ?? 0,
        totalInquiry: pickNumber(monthDoc?.enquiries, monthDoc?.inquiry) ?? 0,
        frozenMembers: currentFrozen ?? 0,
        churnRate: currentChurn ?? 0,
        trainerSales: currentTrainer ?? 0
      });

      setKpis({
        churn: buildKpi(currentChurn, previousChurn, { invert: true, isPercent: true }),
        sales: buildKpi(currentSales, previousSales),
        posSales: buildKpi(currentPos, previousPos),
        dayPassSales: buildKpi(currentDayPassSale, previousDayPassSale),
        activeMembers: buildKpi(currentActive, previousActive),
        newMembers: buildKpi(currentNewMembers, previousNewMembers),
        dayPass: buildKpi(currentVisitors, previousVisitors),
        frozen: buildKpi(currentFrozen, previousFrozen, { invert: true }),
        trainerSales: buildKpi(currentTrainer, previousTrainer)
      });
    } catch (error) {
      console.error("[Dashboard] fetch failed:", error);
      setFetchError(error?.message || "Failed to load dashboard");
      setHasStatsDoc(false);
      setStats(EMPTY_STATS);
      setKpis({
        churn: { ...EMPTY_KPI },
        sales: { ...EMPTY_KPI },
        posSales: { ...EMPTY_KPI },
        dayPassSales: { ...EMPTY_KPI },
        activeMembers: { ...EMPTY_KPI },
        newMembers: { ...EMPTY_KPI },
        dayPass: { ...EMPTY_KPI },
        frozen: { ...EMPTY_KPI },
        trainerSales: { ...EMPTY_KPI }
      });
    } finally {
      setLoading(false);
    }
  }, [branchId, selectedMonth, selectedYear]);

  const resolveBranchId = useCallback(() => {
    if (typeof branchId === "object" && branchId !== null) {
      return String(branchId._id || branchId.id || "").trim();
    }
    return String(branchId || "").trim();
  }, [branchId]);

  const loadChartData = useCallback(async (metricKey) => {
    const config = CHART_METRICS[metricKey];
    const resolvedBranchId = resolveBranchId();
    if (!config || !resolvedBranchId) return;

    setChartLoading(true);
    setChartError("");

    try {
      await ensureProductionApiToken();

      // Sales can use dedicated admin chart API when available
      if (metricKey === "sales") {
        const chartApiData = await _get(
          getMonthWiseSalesChartDataURL(resolvedBranchId, selectedMonth, selectedYear),
          { useCache: false }
        );
        const chartPayload = unwrapPayload(chartApiData);
        const hasApiSeries =
          chartPayload &&
          (Array.isArray(chartPayload.currentYearData) || Array.isArray(chartPayload.previousYearData));

        if (hasApiSeries) {
          setChartSeries({
            currentYear: toNumber(chartPayload.currentYear) ?? selectedYear,
            previousYear: toNumber(chartPayload.previousYear) ?? selectedYear - 1,
            currentYearData: Array.from({ length: 12 }, (_, index) =>
              chartPayload.currentYearData?.[index] ?? null
            ),
            previousYearData: Array.from({ length: 12 }, (_, index) =>
              chartPayload.previousYearData?.[index] ?? null
            )
          });
          return;
        }
      }

      // Active members can use year status endpoint when available
      if (metricKey === "activeMembers") {
        const activeStatusData = await _get(
          getTotalActiveMembersStatusURL(resolvedBranchId, selectedMonth, selectedYear),
          { useCache: false }
        );
        const payload = unwrapPayload(activeStatusData);
        const activeMembersData = Array.isArray(payload?.activeMembersData)
          ? payload.activeMembersData
          : Array.isArray(payload)
            ? payload
            : null;

        if (activeMembersData) {
          const currentYearData = Array.from({ length: 12 }, (_, index) => {
            if (index + 1 > selectedMonth) return null;
            const entry = activeMembersData[index];
            if (typeof entry === "number") return entry;
            return pickNumber(entry?.value, entry?.count, entry?.total) ?? 0;
          });

          setChartSeries({
            currentYear: selectedYear,
            previousYear: selectedYear - 1,
            currentYearData,
            previousYearData: Array(12).fill(null)
          });
          return;
        }
      }

      // Day pass sales come from visitor-report (same source as visitor management)
      if (metricKey === "dayPassSales") {
        const buildYearDayPassSales = async (year, upToMonth = 12) =>
          Promise.all(
            Array.from({ length: 12 }, async (_, index) => {
              const month = index + 1;
              if (month > upToMonth) return null;
              const result = await fetchDayPassSales(resolvedBranchId, month, year);
              if (result.error) return 0;
              return Number(result.total) || 0;
            })
          );

        const requests = [buildYearDayPassSales(selectedYear, selectedMonth)];
        if (config.compareYears) {
          requests.push(buildYearDayPassSales(selectedYear - 1, 12));
        }

        const [currentYearData, previousYearData = Array(12).fill(null)] = await Promise.all(requests);

        setChartSeries({
          currentYear: selectedYear,
          previousYear: selectedYear - 1,
          currentYearData,
          previousYearData
        });
        return;
      }

      // Subscription / Trainer Sales come from subscription fee collection report
      if (metricKey === "trainerSales") {
        const buildYearSubscriptionSales = async (year, upToMonth = 12) =>
          Promise.all(
            Array.from({ length: 12 }, async (_, index) => {
              const month = index + 1;
              if (month > upToMonth) return null;
              const result = await fetchSubscriptionSales(resolvedBranchId, month, year);
              if (result.error) return 0;
              return Number(result.total) || 0;
            })
          );

        const requests = [buildYearSubscriptionSales(selectedYear, selectedMonth)];
        if (config.compareYears) {
          requests.push(buildYearSubscriptionSales(selectedYear - 1, 12));
        }

        const [currentYearData, previousYearData = Array(12).fill(null)] = await Promise.all(requests);

        setChartSeries({
          currentYear: selectedYear,
          previousYear: selectedYear - 1,
          currentYearData,
          previousYearData
        });
        return;
      }

      const requests = [
        buildYearMetricFromMonthStats(resolvedBranchId, selectedYear, metricKey, selectedMonth)
      ];

      if (config.compareYears) {
        requests.push(
          buildYearMetricFromMonthStats(resolvedBranchId, selectedYear - 1, metricKey, 12)
        );
      }

      const [currentYearData, previousYearData = Array(12).fill(null)] = await Promise.all(requests);

      setChartSeries({
        currentYear: selectedYear,
        previousYear: selectedYear - 1,
        currentYearData,
        previousYearData
      });
    } catch (error) {
      console.error(`[Dashboard] ${metricKey} chart failed:`, error);
      setChartError(error?.message || "Failed to load chart");
      setChartSeries(EMPTY_CHART_SERIES);
    } finally {
      setChartLoading(false);
    }
  }, [resolveBranchId, selectedMonth, selectedYear]);

  const handleChartCardClick = useCallback((metricKey) => {
    setActiveChartKey((prev) => (prev === metricKey ? null : metricKey));
  }, []);

  useEffect(() => {
    fetchDashboardData();
    setActiveChartKey(null);
  }, [fetchDashboardData]);

  useEffect(() => {
    if (activeChartKey) {
      loadChartData(activeChartKey);
    }
  }, [activeChartKey, loadChartData]);

  const activeChartConfig = useMemo(() => {
    if (!activeChartKey) return null;
    const config = CHART_METRICS[activeChartKey];
    if (!config) return null;
    if (activeChartKey !== "trainerSales") return config;
    return {
      ...config,
      title: trainerSalesLabel,
      seriesLabel: trainerSalesLabel
    };
  }, [activeChartKey, trainerSalesLabel]);

  const chartData = useMemo(() => {
    if (!activeChartConfig) return { labels: MONTH_CHART_LABELS, datasets: [] };

    const datasets = [
      {
        label: `${chartSeries.currentYear} ${activeChartConfig.seriesLabel}`,
        data: chartSeries.currentYearData,
        borderColor: activeChartConfig.color,
        backgroundColor: activeChartConfig.chartType === "bar"
          ? activeChartConfig.color
          : `${activeChartConfig.color}26`,
        tension: 0.35,
        fill: false,
        spanGaps: false
      }
    ];

    if (activeChartConfig.compareYears) {
      datasets.push({
        label: `${chartSeries.previousYear} ${activeChartConfig.seriesLabel}`,
        data: chartSeries.previousYearData,
        borderColor: activeChartConfig.prevColor,
        backgroundColor: activeChartConfig.chartType === "bar"
          ? activeChartConfig.prevColor
          : `${activeChartConfig.prevColor}26`,
        tension: 0.35,
        fill: false,
        spanGaps: false
      });
    }

    return { labels: MONTH_CHART_LABELS, datasets };
  }, [activeChartConfig, chartSeries]);

  const combinedSales = useMemo(() => {
    const sales = Number(stats.totalSales) || 0;
    const posSales = Number(stats.totalPosSales) || 0;
    const dayPassSales = Number(stats.totalDayPassSales) || 0;
    const trainerSales = Number(stats.trainerSales) || 0;
    return sales + posSales + dayPassSales + trainerSales;
  }, [stats.totalSales, stats.totalPosSales, stats.totalDayPassSales, stats.trainerSales]);

  const chartOptions = useMemo(() => {
    const isCurrency = Boolean(activeChartConfig?.currency);
    const isBar = activeChartConfig?.chartType === "bar";
    const compareYears = Boolean(activeChartConfig?.compareYears);
    const showPersonIcon = activeChartKey === "activeMembers";
    const showRsPrefix =
      activeChartKey === "sales" ||
      activeChartKey === "posSales" ||
      activeChartKey === "dayPassSales" ||
      activeChartKey === "trainerSales";

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 28,
          right: 8
        }
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          labels: {
            color: "#334155",
            boxWidth: 12
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed?.y;
              if (value === null || value === undefined) {
                return `${context.dataset.label}: No Data`;
              }
              return `${context.dataset.label}: ${Number(value).toLocaleString()}`;
            }
          }
        },
        datalabels: {
          display: (context) => {
            const value = context.dataset.data?.[context.dataIndex];
            return value !== null && value !== undefined && value !== "";
          },
          anchor: (context) => {
            if (isBar) return "end";
            return compareYears && context.datasetIndex === 1 ? "start" : "end";
          },
          align: (context) => {
            if (isBar) return "top";
            return compareYears && context.datasetIndex === 1 ? "bottom" : "top";
          },
          offset: 4,
          clamp: true,
          clip: false,
          color: "#334155",
          font: {
            weight: "bold",
            size: 11
          },
          formatter: (value) => {
            if (value === null || value === undefined) return "";
            const formatted = isCurrency
              ? formatShortCurrency(value)
              : Number(value).toLocaleString();
            if (showPersonIcon) return `👤 ${formatted}`;
            if (showRsPrefix) return `Rs ${formatted}`;
            return formatted;
          }
        }
      },
      scales: {
        x: {
          ticks: { color: "#64748b" },
          grid: { color: "rgba(148, 163, 184, 0.2)" }
        },
        y: {
          beginAtZero: true,
          grace: "12%",
          ticks: {
            color: "#64748b",
            callback: (value) => (isCurrency ? formatShortCurrency(value) : Number(value).toLocaleString())
          },
          grid: { color: "rgba(148, 163, 184, 0.2)" }
        }
      }
    };
  }, [activeChartConfig, activeChartKey]);

  const renderKpiCard = (metricKey, title, currentLabel, previousLabel, diffLabel, status, extra = {}) => (
    <KpiCard
      key={metricKey}
      title={title}
      currentLabel={currentLabel}
      previousLabel={previousLabel}
      diffLabel={diffLabel}
      status={status}
      onClick={() => handleChartCardClick(metricKey)}
      active={activeChartKey === metricKey}
      hint={activeChartKey === metricKey ? "Click to hide chart" : "Click to view chart"}
      {...extra}
    />
  );

  return (
    <div className="configMain admin-dashboard">
      <div className="admin-dashboard-header">
        <section className="admin-dashboard-stats" aria-label="Branch month stats">
          <StatCard
            title="Combined Sales"
            value={formatCurrency(combinedSales)}
            subValue={formatAmountInWords(combinedSales)}
          />
          <StatCard title="Total Active Customers" value={formatCount(stats.totalActiveCustomers)} />
          <StatCard title="Defaulters" value={formatCount(stats.defaulters)} />
          <StatCard title="Active Members This Month" value={formatCount(stats.activeMembersThisMonth)} />
        </section>

        <div className="admin-dashboard-filters">
          <div>
            <label>Select Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
            >
              {MONTHS.map((month, index) => (
                <option key={month} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label>Select Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            >
              {YEARS.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="admin-dashboard-loading">Loading dashboard data...</div>
      )}

      {!loading && fetchError && (
        <div className="admin-dashboard-empty">{fetchError}</div>
      )}

      {!loading && !fetchError && !hasStatsDoc && (
        <div className="admin-dashboard-empty">
          No statsDashboard record found for {MONTHS[selectedMonth - 1]} {selectedYear}.
          Try another month/year.
        </div>
      )}

      <section className="admin-dashboard-kpi-section" aria-label="Key indicators">
        <h2>Key Indicators</h2>
        <p className="admin-dashboard-kpi-subtitle">Compared with previous month from statsDashboard</p>

        <div className="admin-dashboard-kpi-grid">
          {renderKpiCard(
            "sales",
            "Sales",
            formatCurrency(kpis.sales.current),
            formatCurrency(kpis.sales.previous),
            formatCurrency(kpis.sales.diff),
            kpis.sales.status
          )}
          {renderKpiCard(
            "activeMembers",
            "Active Members",
            formatCount(kpis.activeMembers.current),
            formatCount(kpis.activeMembers.previous),
            formatCount(kpis.activeMembers.diff),
            kpis.activeMembers.status
          )}
          {renderKpiCard(
            "newMembers",
            "New Members",
            formatCount(kpis.newMembers.current),
            formatCount(kpis.newMembers.previous),
            formatCount(kpis.newMembers.diff),
            kpis.newMembers.status
          )}
          {renderKpiCard(
            "dayPass",
            "Day Pass",
            formatCount(kpis.dayPass.current),
            formatCount(kpis.dayPass.previous),
            formatCount(kpis.dayPass.diff),
            kpis.dayPass.status
          )}
          {renderKpiCard(
            "frozen",
            "Frozen Members",
            formatCount(kpis.frozen.current),
            formatCount(kpis.frozen.previous),
            formatCount(kpis.frozen.diff),
            kpis.frozen.status,
            { invertColors: true }
          )}
          {renderKpiCard(
            "trainerSales",
            trainerSalesLabel,
            formatCurrency(kpis.trainerSales.current),
            formatCurrency(kpis.trainerSales.previous),
            formatCurrency(kpis.trainerSales.diff),
            kpis.trainerSales.status
          )}
          {renderKpiCard(
            "posSales",
            "POS Sales",
            formatCurrency(kpis.posSales.current),
            formatCurrency(kpis.posSales.previous),
            formatCurrency(kpis.posSales.diff),
            kpis.posSales.status
          )}
          {renderKpiCard(
            "dayPassSales",
            "Day Pass Sales",
            formatCurrency(kpis.dayPassSales.current),
            formatCurrency(kpis.dayPassSales.previous),
            formatCurrency(kpis.dayPassSales.diff),
            kpis.dayPassSales.status
          )}
        </div>

        {activeChartKey && activeChartConfig && (
          <div className="admin-dashboard-sales-chart">
            <div className="admin-dashboard-sales-chart-header">
              <h3>
                {activeChartConfig.title}
                {activeChartConfig.compareYears
                  ? ` ${chartSeries.previousYear}-${String(chartSeries.currentYear).slice(-2)}`
                  : ` ${chartSeries.currentYear}`}
              </h3>
              <button type="button" onClick={() => setActiveChartKey(null)}>Close</button>
            </div>
            {chartLoading && (
              <div className="admin-dashboard-loading">Loading chart...</div>
            )}
            {!chartLoading && chartError && (
              <div className="admin-dashboard-empty">{chartError}</div>
            )}
            {!chartLoading && !chartError && (
              <div className="admin-dashboard-sales-chart-canvas">
                {activeChartConfig.chartType === "bar" ? (
                  <Bar data={chartData} options={chartOptions} />
                ) : (
                  <Line data={chartData} options={chartOptions} />
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default Dashboard;
